<template>
  <div id="pageReporte5">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <v-toolbar color="primary" dark flat class="report-toolbar">
              <v-icon class="mr-2">mdi-car-crash</v-icon>
              <v-toolbar-title>Daños detectados</v-toolbar-title>
              <v-spacer></v-spacer>
              <report-filters :fields="['period', 'location_id', 'condition', 'responsible_id']" @change="onFilterChange"></report-filters>
              <v-btn outlined :loading="loadingExport" @click="downloadExcel">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                <span class="d-none d-sm-inline">Descargar Excel</span>
              </v-btn>
              <v-btn outlined class="ml-2" :loading="loadingPdf" @click="downloadPdf(5)">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                <span class="d-none d-sm-inline">Descargar PDF</span>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <div class="text-body-2 grey--text text--darken-1 mb-6">
                Daños registrados en entregas y recepciones. Un daño que aparece en la recepción y no constaba en la entrega
                indica que se produjo <strong>durante el traslado</strong>.
              </div>

              <v-progress-linear v-if="loadingPreview" indeterminate color="primary" class="mb-6"></v-progress-linear>

              <div v-else>
                <v-alert v-if="!damages.length && !transfers.length" type="info" text dense>
                  No hay datos para mostrar
                </v-alert>

                <template v-else>
                  <!-- KPIs -->
                  <div class="kpi-grid mb-4">
                    <div class="kpi-card">
                      <v-icon size="30" color="blue darken-2">mdi-car-search</v-icon>
                      <div class="kpi-number">{{ kpis.total }}</div>
                      <div class="kpi-label">Daños registrados</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="red darken-1">mdi-truck-alert</v-icon>
                      <div class="kpi-number">{{ kpis.transit }}</div>
                      <div class="kpi-label">Producidos en el traslado</div>
                      <div class="kpi-subtitle">detectados en recepción</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="orange darken-1">mdi-clipboard-check-outline</v-icon>
                      <div class="kpi-number">{{ kpis.preexisting }}</div>
                      <div class="kpi-label">Ya existentes</div>
                      <div class="kpi-subtitle">detectados en entrega</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="purple darken-1">mdi-percent</v-icon>
                      <div class="kpi-number">{{ kpis.pctTransfers }}</div>
                      <div class="kpi-label">Traslados con daño</div>
                      <div class="kpi-subtitle">sobre {{ kpis.transfers }} movimientos</div>
                    </div>
                  </div>

                  <v-alert v-if="kpis.unclassified > 0" type="info" text dense border="left" class="mb-4">
                    {{ kpis.unclassified }} {{ kpis.unclassified === 1 ? 'daño' : 'daños' }} sin clasificar (fotos antiguas sin
                    momento registrado): cuentan en el total pero no en "Producidos en el traslado" ni en "Ya existentes".
                  </v-alert>

                  <v-row class="mb-2">
                    <!-- Por trayecto -->
                    <v-col cols="12" md="6">
                      <div class="report-section-title">
                        <v-icon small color="primary" class="mr-1">mdi-sign-direction</v-icon>
                        Por trayecto
                      </div>
                      <v-simple-table class="report-table" dense>
                        <thead>
                          <tr>
                            <th>Trayecto</th>
                            <th class="text-center">Traslados</th>
                            <th class="text-center">Daños en traslado</th>
                            <th class="text-center">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in byRoute" :key="row.route">
                            <td>{{ row.route }}</td>
                            <td class="text-center">{{ row.transfers }}</td>
                            <td class="text-center">{{ row.transit }}</td>
                            <td class="text-center">
                              <span class="bucket-pill" :class="pctClass(row.pct)">{{ formatPct(row.pct) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>

                    <!-- Por conductor / proveedor -->
                    <v-col cols="12" md="6">
                      <div class="report-section-title">
                        <v-icon small color="primary" class="mr-1">mdi-steering</v-icon>
                        Por conductor / proveedor
                      </div>
                      <v-simple-table class="report-table" dense>
                        <thead>
                          <tr>
                            <th>Conductor / proveedor</th>
                            <th>Tipo</th>
                            <th class="text-center">Traslados</th>
                            <th class="text-center">Daños</th>
                            <th class="text-center">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in byCarrier" :key="row.carrier">
                            <td>{{ row.carrier }}</td>
                            <td>{{ row.type }}</td>
                            <td class="text-center">{{ row.transfers }}</td>
                            <td class="text-center">{{ row.transit }}</td>
                            <td class="text-center">
                              <span class="bucket-pill" :class="pctClass(row.pct)">{{ formatPct(row.pct) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>

                  <!-- Por ubicación -->
                  <div class="report-section-title mt-4">
                    <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                    Por ubicación
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Ubicación</th>
                        <th class="text-center">Detectados al entregar (como origen)</th>
                        <th class="text-center">Detectados al recepcionar (como destino)</th>
                        <th class="text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byLocation" :key="row.location">
                        <td class="font-weight-bold">{{ row.location }}</td>
                        <td class="text-center">{{ row.delivery }}</td>
                        <td class="text-center">
                          <span v-if="row.reception > 0" class="bucket-pill" :class="row.transit === row.reception ? 'bucket-3' : 'bucket-2'">{{ row.reception }}</span>
                          <span v-else>0</span>
                          <div v-if="row.reception > 0" class="text-caption grey--text">{{ row.transit }} en traslado</div>
                        </td>
                        <td class="text-center font-weight-bold">{{ row.delivery + row.reception }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Detalle -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>
                    Detalle de daños
                  </div>
                  <v-alert v-if="!damages.length" type="success" text dense>
                    No se ha registrado ningún daño
                  </v-alert>
                  <v-data-table
                    v-else
                    :headers="detailHeaders"
                    :items="damages"
                    class="report-table report-table--link mb-4"
                    @click:row="row => openRecord(row, 'movement')"
                    dense
                    item-key="id"
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                    :items-per-page="10"
                  >
                    <template v-slot:item.vehicle="{ item }">
                      <div class="font-weight-medium">{{ item.vehicle }}</div>
                      <div class="text-caption grey--text">Matrícula: {{ item.plate || '—' }}</div>
                      <div class="text-caption grey--text">Bastidor: {{ item.frame || '—' }}</div>
                    </template>
                    <template v-slot:item.id="{ item }">
                      <div>{{ item.id }}</div>
                      <div class="text-caption grey--text">{{ item.movement_id }}</div>
                    </template>
                    <template v-slot:item.moment="{ item }">
                      <v-chip small :color="momentChip(item).color" :text-color="momentChip(item).textColor" class="moment-chip">
                        {{ momentChip(item).text }}
                      </v-chip>
                    </template>
                    <template v-slot:item.date="{ item }">
                      {{ formatDateOnly(item.date) }}
                    </template>
                    <template v-slot:item.carrier="{ item }">
                      <div>{{ item.carrier }}</div>
                      <div class="text-caption grey--text">{{ item.carrier_type }}</div>
                    </template>
                  </v-data-table>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import ReportService from '../../services/ReportService'
import errorHandler from '../../mixins/errorHandler'
import reportPdf from '../../mixins/reportPdf'
import reportNavigation from '../../mixins/reportNavigation'
import ReportFilters from '../../components/ReportFilters'

const PREEXISTING = 'Preexistente'
const NO_LOCATION = 'Sin ubicación'

const isTransit = (damage) => (damage.classification || '').startsWith('Producido')
const isPreexisting = (damage) => damage.classification === PREEXISTING
const sortByTransitThenPct = (a, b) => b.transit - a.transit || b.pct - a.pct

export default {
  name: 'Reporte5',

  components: { ReportFilters },

  mixins: [errorHandler, reportPdf, reportNavigation],

  data: () => ({
    params: { period: 'this_month' },
    damages: [],
    transfers: [],
    loadingPreview: true,
    loadingExport: false,

    detailHeaders: [
      { text: 'Vehículo', value: 'vehicle' },
      { text: 'Trayecto', value: 'route' },
      { text: 'Daño', value: 'id' },
      { text: 'Momento', value: 'moment' },
      { text: 'Fecha', value: 'date' },
      { text: 'Registrado por', value: 'registered_by' },
      { text: 'Conductor / proveedor', value: 'carrier' }
    ]
  }),

  computed: {
    kpis () {
      const total = this.damages.length
      const transit = this.damages.filter(isTransit).length
      const preexisting = this.damages.filter(isPreexisting).length
      const transfers = this.transfers.length
      const withDamage = this.transfers.filter(t => t.transit_damages > 0).length

      return {
        total,
        transit,
        preexisting,
        unclassified: total - transit - preexisting,
        transfers,
        pctTransfers: transfers ? `${Math.round(withDamage * 100 / transfers)} %` : '—'
      }
    },

    // Daños entre traslados: un traslado con varios daños puede superar el 100 %
    byRoute () {
      const groups = {}
      this.transfers.forEach(t => {
        if (!groups[t.route]) groups[t.route] = { route: t.route, transfers: 0, transit: 0 }
        groups[t.route].transfers++
      })
      this.damages.filter(isTransit).forEach(d => {
        if (!groups[d.route]) groups[d.route] = { route: d.route, transfers: 0, transit: 0 }
        groups[d.route].transit++
      })

      return Object.values(groups)
        .map(row => ({ ...row, pct: row.transfers ? row.transit * 100 / row.transfers : 0 }))
        .sort(sortByTransitThenPct)
    },

    byCarrier () {
      const groups = {}
      const ensure = (carrier, type) => {
        if (!groups[carrier]) groups[carrier] = { carrier, type, transfers: 0, transit: 0 }
        return groups[carrier]
      }
      this.transfers.forEach(t => { ensure(t.carrier, t.carrier_type).transfers++ })
      this.damages.filter(isTransit).forEach(d => { ensure(d.carrier, d.carrier_type).transit++ })

      return Object.values(groups)
        .map(row => ({ ...row, pct: row.transfers ? row.transit * 100 / row.transfers : 0 }))
        .sort(sortByTransitThenPct)
    },

    byLocation () {
      const groups = {}
      const ensure = (location) => {
        const key = location || NO_LOCATION
        if (!groups[key]) groups[key] = { location: key, delivery: 0, reception: 0, transit: 0 }
        return groups[key]
      }
      this.damages.forEach(d => {
        if (d.moment === 'Entrega') {
          ensure(d.origin).delivery++
        } else if (d.moment === 'Recepción') {
          const row = ensure(d.destination)
          row.reception++
          if (isTransit(d)) row.transit++
        }
      })

      return Object.values(groups).sort((a, b) => (b.delivery + b.reception) - (a.delivery + a.reception))
    }
  },

  methods: {
    onFilterChange (params) {
      this.params = params
      this.loadPreview()
    },

    pctClass (pct) {
      if (pct >= 25) return 'bucket-3'
      if (pct >= 10) return 'bucket-2'
      if (pct >= 7) return 'bucket-1'
      return 'bucket-0'
    },

    formatPct (pct) {
      return `${Math.round(pct)} %`
    },

    momentChip (damage) {
      if (isTransit(damage)) return { text: `${damage.moment} · en traslado`, color: 'red lighten-5', textColor: 'red darken-2' }
      if (isPreexisting(damage)) return { text: `${damage.moment} · ya existente`, color: 'blue lighten-5', textColor: 'blue darken-3' }
      return { text: damage.moment || 'Sin especificar', color: 'grey lighten-3', textColor: 'grey darken-2' }
    },

    formatDateOnly (value) {
      if (!value) return '—'
      return moment.unix(value).format('DD/MM/YYYY')
    },

    async loadPreview () {
      try {
        this.loadingPreview = true
        const { data } = await ReportService.getReport5(this.params)
        const payload = data.data || {}
        this.damages = payload.damages || []
        this.transfers = payload.transfers || []
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los datos del reporte')
      } finally {
        this.loadingPreview = false
      }
    },

    async downloadExcel () {
      try {
        this.loadingExport = true
        const response = await ReportService.downloadReport5(this.params)
        const blob = response.data

        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
        link.download = `reporte5_${timestamp}.xlsx`
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Reporte descargado correctamente')
      } catch (error) {
        this.handleApiError(error, 'Error al descargar el reporte')
      } finally {
        this.loadingExport = false
      }
    }
  },

  created () {
    this.loadPreview()
  }
}
</script>

<style scoped>
.report-table--link >>> tbody tr {
  cursor: pointer;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.kpi-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 12px;
  text-align: center;
}

.kpi-number {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.kpi-subtitle {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.report-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.report-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.report-table >>> thead th {
  background-color: #fafafa;
  font-weight: 600 !important;
}

.moment-chip {
  font-weight: 500;
}

.bucket-pill {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
}

.bucket-0 {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.bucket-1 {
  background-color: #FFF8E1;
  color: #F9A825;
}

.bucket-2 {
  background-color: #FFF3E0;
  color: #EF6C00;
}

.bucket-3 {
  background-color: #FFEBEE;
  color: #C62828;
}
</style>
